import { useCallback, useEffect, useState, useMemo } from 'react';
import moment from 'moment';
import { useIntl, FormattedMessage, FormattedNumber, FormattedDate, FormattedTime } from 'react-intl';
import { makeStyles, Divider, ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, IconButton, Tooltip, Typography, Grid, Button } from '@ellucian/react-design-system/core';
import get from 'lodash/get';
import {isEmpty} from 'lodash';
import useApplicationForm from './useApplicationForm';
import { CONSTANTS, routes } from '../Helpers/Configuration';
import {
    colorTextNeutral200,
    sizingXLarge,
    spacing60,
    spacing70,
    spacing50,
    spacing40,
    spacing30,
    spacing20
} from '@ellucian/react-design-system/core/styles/tokens';
import { useSelector, useDispatch, batch } from 'react-redux';
import { setError, setForceShowErrors } from '@nsr/react-form';
import { isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
import { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice';
import { generatePath, matchPath, useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useApi } from '../ApiContext';
import { isUnderMinimumAge, parseDateValue } from '../Helpers/dateValidationHelpers';

import { GlobalStyles } from 'tss-react';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import SectionLabel from './SectionLabel';
import QuestionResponse from './QuestionResponse';
import ConditionalLogicCheck, { isConditionallyRequiredContext } from './ConditionalLogicCheck';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
const useStyles = makeStyles({
    name: 'Review'
})(theme => ({
    content: {
        display: 'grid',
        gridAutoRows: 'min-content',
        gridGap: spacing50,
        padding: spacing20,
        [theme.breakpoints.down('md')]: {
            padding: 0,
        },
    },
    hide: {
        display: 'none',
    },
    pageDivider: {
        margin: [0, '!important'],
    },
    expansionPanelSummaryText: {
        paddingLeft: spacing60,
    },
    repeatingCount: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    repeatingResponse: {
        display: 'grid',
        gridGap: spacing30,
        width: '100%',
        background: colorTextNeutral200,
        padding: `${spacing30} ${spacing40} ${spacing30} ${spacing40}`,
    },
    responseContainer: {
        display: 'grid',
        gridGap: spacing60,
        width: '100%',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
    },
    sectionTitle: {
        display: 'grid',
        gridAutoColumns: 'max-content',
        gridAutoFlow: 'column',
        gridGap: spacing30,
        marginBottom: spacing40,
    },
    summaryEditPageButton: {
        marginLeft: 'auto',
    },
    summaryContent: {
        '& > div': {
            alignItems: 'center',
        },
    },
    sectionResponse: {
        display: 'grid',
        gridRowGap: spacing30,
    },
    repeatingSectionTitleContainer: {
        display: 'flex',
        alignItems: 'center',
        height: sizingXLarge,
        marginBottom: spacing30,
    },
    groupRoot: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridColumnGap: spacing70,
        gridTemplateColumns: 'repeat(3, 1fr)',
        marginBottom: spacing50,
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
            gridRowGap: spacing50,
            marginBottom: spacing50,
        },
    },
    question: {
        paddingLeft: spacing30,
    },
    continue: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: spacing40,
        [theme.breakpoints.down('md')]: {
            justifyContent: 'flex-end',
        },
    },
}));
const selectAllValues = state => state.form.values;
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectApplicationIsArchived = state => state.application?.archivedOn;
const selectApplicationIsWithdrawn = state => state.application?.status === CONSTANTS.applicationFormStatuses.WITHDRAWN;
const selectPiiHidden = state => state.application?.isPiiHidden;
const selectApplicationPayment = state => state.application.paymentInformation;
const selectApplication = state => state.application;
const selectApplicationId = state => state.application.id;
const isBirthDateQuestion = (question) => question?.eedmDestinationAttribute?.toLowerCase?.().endsWith('/dateofbirth');
const defaultStartYear = moment().subtract(30, 'years').year();

const Review = props => {
    const intl = useIntl();
    const { onPageClick: onPageClickProp, adminMode, applicationValues } = props;
    const {
        supplementalFormMode,
        template,
        getApplicationFee,
    } = useApplicationForm();
    const { classes } = useStyles();
    const api = useApi();
    const values = applicationValues ? applicationValues : useSelector(selectAllValues);
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const isArchived = useSelector(selectApplicationIsArchived);
    const isWithdrawn = useSelector(selectApplicationIsWithdrawn);
    const piiHidden = useSelector(selectPiiHidden);
    const paymentInformation = useSelector(selectApplicationPayment);
    const application = useSelector(selectApplication);
    const applicationId = useSelector(selectApplicationId);
    const [shouldPaymentShow, setShouldPaymentShow] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const receipt = useMemo(() => {
        if (isEmpty(paymentInformation)) {
            return null;
        }
        let appliedDiscountValue = paymentInformation?.appliedDiscountValue?? 0;
        const entries = Object.entries({
            'applicationReviewTransactionId': paymentInformation?.transactionId,
            'applicationReviewDateAndTime': application?.submittedOn || '',
            'applicationReviewApplicationFee': paymentInformation?.amount,
            'applicationReviewDiscountValue': appliedDiscountValue,
            'applicationReviewAmountPaid': appliedDiscountValue >= paymentInformation?.amount? 0 : paymentInformation?.amount - appliedDiscountValue,
            'applicationReviewPaymentStatus': paymentInformation?.paymentStatus
        });
        return entries;
    }, [paymentInformation, application]);
    const getValue = useCallback((questionId, pageId) => {
        return get(values, `${pageId}.${questionId}`, null);
    }, [values]);

    const [expandedList, setExpandedList] = useState(() => {
        return template.pages.map((t, i) => (isArchived || isSubmitted) || i === 0);
    });

    useEffect(() => {
        const pageTitle = document.getElementById('page-title');
        if (pageTitle) {
            pageTitle.focus();
        }
    }, []);

    useEffect(() => {
        if (isSubmitted || isArchived) {
            dispatch(stopLoading());
            setExpandedList(expandedList.map(() => true));
        }
    }, [isSubmitted, isArchived]);

    const onPageClick = useCallback(pageId => {
        onPageClickProp && onPageClickProp(pageId);
    });

    const handleExpandPanel = useCallback(idx => {
        expandedList[idx] = !expandedList[idx];
        setExpandedList([...expandedList]);
    }, [expandedList]);

    // date validation function
    const getDateValidationError = useCallback((question, value) => {
        if (!value || typeof value.getFullYear !== 'function') {
            return undefined;
        }

        let errorMessage = undefined;
        const yyyy = value.getFullYear();

        // Year < 100 = 2-digit year typo (e.g. "24-06-26" → year 24 CE).
        if (!errorMessage && yyyy < 100) {
            errorMessage = intl.formatMessage({ id: 'invalidDateBeforeStartYear' }, { year: `01/01/${question.startYear || defaultStartYear}` });
        }

        // Enforce minimum age by rejecting dates that make the applicant too young
        if (!errorMessage && isBirthDateQuestion(question) && isUnderMinimumAge(question.minAge, value)) {
            errorMessage = intl.formatMessage({ id: 'invalidDateForMinimumAge' }, { age: question.minAge });
        }

        return errorMessage;
    }, [intl]);

    // Re-validate all date fields on mount/refresh and when values change.
    // On refresh, stored values are strings ("0023-04-25") — parseDateValue converts them to Date objects.
    useEffect(() => {
        if (isSubmitted || isArchived || isWithdrawn || !template?.pages?.length) {
            return;
        }

        const errors = [];
        template.pages.forEach(page => {
            page?.sections?.forEach(section => {
                section?.groups?.forEach(group => {
                    group?.questions?.forEach(question => {
                        if (question?.type !== CONSTANTS.controlTypes.datepicker) {
                            return;
                        }

                        const fieldPath = `${page.id}.${question.id}`;
                        const rawValue = get(values, fieldPath);
                        if (!rawValue) {
                            return;
                        }

                        const dateValue = parseDateValue(rawValue);
                        const error = dateValue && getDateValidationError(question, dateValue);
                        if (error) {
                            errors.push({ fieldPath, pageId: page.id, error });
                        }
                    });
                });
            });
        });

        if (errors.length > 0) {
            batch(() => {
                errors.forEach(({ fieldPath, pageId, error }) => {
                    dispatch(setError({ name: fieldPath, value: error }));
                    dispatch(setForceShowErrors({ name: pageId, value: true }));
                });
            });
        }
    }, [template?.pages, values, isSubmitted, isArchived, isWithdrawn, dispatch, getDateValidationError]);

    const renderValue = (value, key) => {
        if (['applicationReviewApplicationFee', 'applicationReviewDiscountValue', 'applicationReviewAmountPaid'].includes(key)) {
            return <FormattedNumber value={value} style="currency" currency={paymentInformation.currency} />;
        } else if (key === 'applicationReviewDateAndTime') {
            return <><FormattedDate value={value}/> <FormattedTime value={value} /></>;
        } else {
            return value;
        }
    };

    useEffect(() => {
        const fn = async () => {
            try {
                const isOnSubmitted = matchPath(history.location.pathname, {
                    path: [
                        routes.applicationFormSubmitPage
                    ]
                });
                if (isOnSubmitted) {
                    dispatch(startLoading());
                    const result = await getApplicationFee(values);
                    if (result) {
                        const showPayment = await api.applicationForms.checkForSubmittedPayment(application.id);
                        setShouldPaymentShow(showPayment);
                    }
                    dispatch(stopLoading());
                }
            } catch (e) {
                console.error(e);
                dispatch(stopLoading());
            }
        };
        fn();
    }, [values, dispatch]);

    return (
        <>
            <GlobalStyles styles={{ '.eds-expansion-panel-icon-left-position': { top: 'auto !important' } }} />
            <main className={classes.content}>
                {!supplementalFormMode && !adminMode &&
                <>
                    {!isSubmitted && !isArchived &&
                        <>
                            <Typography 
                                variant="h2" 
                                id="page-title"
                                tabIndex="-1"
                            >
                                <FormattedMessage id="reviewAndSubmit" />
                            </Typography>
                            <Divider className={classes.pageDivider} />
                        </>
                    }
                    {(isSubmitted || isArchived) &&
                        <>
                            <Typography variant="h2">
                                <FormattedMessage id="applicationReadonlyView" />
                            </Typography>
                            <Divider className={classes.pageDivider} />
                        </>
                    }
                </>
                }
                <div>
                    {template.pages.map((page, idx) => (
                        <ConditionalLogicCheck key={page.id} conditions={page.conditions}>
                            <ExpansionPanel
                                onChange={() => {
                                    handleExpandPanel(idx);
                                }}
                                expanded={expandedList[idx]}
                            >
                                <ExpansionPanelSummary
                                    className={classes.summaryContent}
                                    expandIconProps={{ id: 'expandList' }}
                                    leftIconDisplay={true}
                                >
                                    <Typography variant="h3" className={classes.expansionPanelSummaryText}>
                                        <FormattedMessage id={page.titleId} />
                                    </Typography>
                                    {!isSubmitted && !isArchived && isWithdrawn && !adminMode && page.type !== CONSTANTS.addNewPageType.welcome &&
                                    <Tooltip title={intl.formatMessage({ id: 'editPage' })}>
                                        <IconButton
                                            aria-label={intl.formatMessage({ id: 'editPage' })}
                                            className={classes.summaryEditPageButton}
                                            color="gray"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onPageClick(page.id);
                                            }}
                                        >
                                            <Icon name="edit" />
                                        </IconButton>
                                    </Tooltip>
                                    }
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails>
                                    <Typography className={classes.responseContainer} component="div">
                                        <div>
                                            {
                                                page.showDescription && page.descriptionId &&
                                            <div><HtmlFormattedMessage descriptor={{ id: page.descriptionId }} normalizeSpaces /></div>
                                            }
                                        </div>
                                        {page.sections?.map(section => (
                                            <ConditionalLogicCheck key={section.id} conditions={section.conditions}>
                                                <div className={classes.sectionResponse}>
                                                    {section.showTitle && section.titleId &&
                                                    <SectionLabel>
                                                        <FormattedMessage id={section.titleId} />
                                                    </SectionLabel>
                                                    }
                                                    {!section.showRepetition && section.groups?.map(group => (
                                                        <div key={group.id} className={classes.groupRoot}>
                                                            {group.questions?.map(question => (
                                                                <ConditionalLogicCheck key={question.id}
                                                                    conditions={question.conditions}
                                                                    content={question}
                                                                    contentType={CONSTANTS.appTemplateControlTypes.question}
                                                                    sectionId={section.id}>
                                                                    <div className={classes.question}>{!question.hidden &&
                                                                    <isConditionallyRequiredContext.Consumer>
                                                                        {isConditionallyRequired =>
                                                                            <QuestionResponse
                                                                                question={question}
                                                                                prefix={page.id}
                                                                                value={getValue(question.id, page.id)}
                                                                                piiHidden={piiHidden}
                                                                                isConditionallyRequired={isConditionallyRequired}
                                                                            />}
                                                                    </isConditionallyRequiredContext.Consumer>
                                                                    }</div>
                                                                </ConditionalLogicCheck>
                                                            ))}
                                                        </div>
                                                    ))}
                                                    {section.showRepetition && values?.[page.id]?.[section.id]?.map((repeatingSection, idx) => {
                                                        return (
                                                            <div key={idx} className={classes.repeatingResponse}>
                                                                {section.showRepetitionTitle && section.repetitionTitleId &&
                                                                <Typography className={classes.repeatingSectionTitleContainer} component="div" variant="body2">
                                                                    <FormattedMessage id="repeatingSectionTitleWithCount" values={{
                                                                        title: intl.formatMessage({ id: section.repetitionTitleId }),
                                                                        count: idx + 1,
                                                                    }} />
                                                                </Typography>
                                                                }
                                                                {section.groups?.map(group => {
                                                                    return (
                                                                        <div
                                                                            key={`${group.id}.${idx}`}
                                                                            className={classes.groupRoot}
                                                                        >
                                                                            {group.questions?.map(question => (
                                                                                <ConditionalLogicCheck
                                                                                    key={`${group.id}.${idx}.${question.id}`}
                                                                                    prefix={`${idx}`}
                                                                                    conditions={question.conditions}
                                                                                    content={question}
                                                                                    contentType={CONSTANTS.appTemplateControlTypes.question}
                                                                                    sectionId={section.id}
                                                                                    repeatingSection={section.showRepetition}
                                                                                >
                                                                                    <div className={classes.question}>{!question.hidden &&
                                                                                    <isConditionallyRequiredContext.Consumer>
                                                                                        {isConditionallyRequired =>
                                                                                            <QuestionResponse
                                                                                                question={question}
                                                                                                prefix={`${page.id}.${section.id}.${idx}`}
                                                                                                value={repeatingSection[question.id]}
                                                                                                piiHidden={piiHidden}
                                                                                                isConditionallyRequired={isConditionallyRequired}
                                                                                            />}
                                                                                    </isConditionallyRequiredContext.Consumer>
                                                                                    }</div>
                                                                                </ConditionalLogicCheck>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </ConditionalLogicCheck>
                                        ))}
                                    </Typography>
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        </ConditionalLogicCheck>
                    ))}
                    {isSubmitted && !adminMode && paymentInformation && (paymentInformation.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.successful) &&
                    <ExpansionPanel defaultExpanded={true}>
                        <ExpansionPanelSummary
                            className={classes.summaryContent}
                            expandIconProps={{ id: 'expandList' }}
                            leftIconDisplay={true}
                        >
                            <Typography component="div" variant="h4" className={classes.expansionPanelSummaryText}>
                                <FormattedMessage id="applicationReviewPaymentReceiptHeader"/>
                            </Typography>
                        </ExpansionPanelSummary>
                        <ExpansionPanelDetails>
                            <Typography  component="div">
                                <Grid container spacing={spacing50}>
                                    {receipt.map(([key, value], index) => (
                                        <Grid item className={classes.valueContainer} xs={12} lg={6} key={index}>
                                            <Typography variant="body3">{intl.formatMessage({id: key})}</Typography>
                                            <Typography variant="body1">{renderValue(value,key)}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Typography>
                        </ExpansionPanelDetails>
                    </ExpansionPanel>
                    }
                </div>
                <>
                    {shouldPaymentShow &&
                    <div className={classes.continue}>
                        <Button onClick={() => 
                            history.push(generatePath(routes.applicationFormPayments, { id: applicationId }))
                        }>
                            <FormattedMessage id="continueToPayment" />
                        </Button>
                    </div>
                    }
                </>
            </main>
        </>
    );
};
export default Review;
